import request from "@/tool/request";
import API from "@/tool/api";
import { getResultData, getResultError, getResultMessage, isResultSuccess, unwrapResult } from "@/tool/result";
import developmentSmsCode from "@/tool/developmentSmsCode.cjs";
import photographyCatalog from "@/tool/photographyCatalog.cjs";
import photographyBooking from "@/tool/photographyBooking.cjs";
import photographyPayment from "@/tool/photographyPayment.cjs";
const { APPOINTMENT, PAYMENT, SERVICE, ASSIGNMENT } = photographyPayment.STATUS_CODES;
import creatorOrderHall from "@/tool/creatorOrderHall.cjs";
import creatorServiceOrder from "@/tool/creatorServiceOrder.cjs";
import creatorArrival from "@/tool/creatorArrival.cjs";
import creatorRevenue from "@/tool/creatorRevenue.cjs";
import creatorDeposit from "@/tool/creatorDeposit.cjs";
import qs from "qs";

// v91 ownership is explicit; never probe another assignee's fulfillment to infer it.
const attachCreatorCompletionFacts = async (page) => ({
    ...page,
    items: await Promise.all((page.items || []).map(async (item) => {
        if (item.currentlyAssigned !== true || item.assignmentStatus !== ASSIGNMENT.IN_SERVICE) return item;
        try {
            const fulfillment = unwrapResult(await request.get(API.photography.creatorFulfillment(item.serviceOrderNo), {
                headers: { "Cache-Control": "no-store" },
            }), "履约信息读取失败");
            return { ...item, fulfillment };
        } catch (error) {
            if (Number(error.code || (error.response && error.response.status)) === 403) return { ...item, fulfillment: null };
            throw error;
        }
    })),
});

const {
    getCatalogRequestState,
    normalizePhotographyCatalog,
    normalizePhotographyThemeDetail,
    PHOTOGRAPHY_CATALOG_STATUS,
} = photographyCatalog;
const emptyCatalog = () => ({ themeTypes: [] });
const {
    CREATOR_HALL_STATUS,
    getCreatorHallRequestState,
    getErrorStatus,
    normalizeCreatorInvitation,
    normalizeCreatorJob,
    normalizeCreatorReadiness,
} = creatorOrderHall;
const emptyCreatorOrderHall = () => ({
    status: CREATOR_HALL_STATUS.IDLE,
    error: "",
    readiness: {
        photographer: normalizeCreatorReadiness(null, "photographer", "摄影师身份尚未准备"),
        makeup: normalizeCreatorReadiness(null, "makeup", "化妆师身份尚未准备"),
    },
    invitations: [],
    orders: [],
});
const loadReadiness = (path, roleKey) => request.get(path)
    .then((response) => normalizeCreatorReadiness(unwrapResult(response, "接单准备状态加载失败"), roleKey))
    .catch((error) => {
        if (getErrorStatus(error) !== 409) throw error;
        return normalizeCreatorReadiness(null, roleKey, getResultMessage(error, "尚未取得该专业身份"));
    });
const refreshAfterMutation = (dispatch, result) => dispatch("loadCreatorOrderHall")
    .then(() => result)
    .catch(() => result);
const {
    buildCreatorServiceOrderQuery,
    buildCreatorDisputePage,
    CREATOR_SERVICE_ORDER_STATUS,
    getCreatorServiceOrderRequestState,
    normalizeCreatorServiceOrder,
    normalizeCreatorServiceOrderPage,
} = creatorServiceOrder;
const loadCreatorDisputeOrders = (criteria, pageNo = 1, collected = []) => request.get(API.photography.creatorServiceOrders, {
    params: buildCreatorServiceOrderQuery({ ...criteria, status: "ALL", pageNo, pageSize: 50 }),
}).then((response) => {
    const page = normalizeCreatorServiceOrderPage(unwrapResult(response, "异常与售后订单加载失败"));
    const combined = collected.concat(page.items);
    if (page.items.length && combined.length < page.total) return loadCreatorDisputeOrders(criteria, pageNo + 1, combined);
    return buildCreatorDisputePage(combined, criteria);
});
const { buildArrivalSubmission } = creatorArrival;
const emptyCreatorServiceOrders = () => ({
    status: CREATOR_SERVICE_ORDER_STATUS.IDLE,
    error: "",
    orders: [],
    selected: null,
    detailRequestId: 0,
    total: 0,
    pageNo: 1,
    pageSize: 20,
    criteria: {},
});
const {
    buildCreatorRevenueRoleIndex,
    normalizeCreatorRevenue,
    normalizeCreatorRevenueDetail,
} = creatorRevenue;
const {
    buildCreatorDepositAgreementAcceptance,
    buildCreatorDepositPaymentSubmission,
    buildCreatorDepositPaymentSwitchSubmission,
    findActiveCreatorDepositPayment,
    getCreatorDepositMeta,
    getOrCreateCreatorDepositIntent,
    isCreatorDepositPaymentTerminal,
    normalizeCreatorDepositAccount,
    normalizeCreatorDepositPaymentChannels,
    normalizeCreatorDepositPaymentRequest,
    normalizeCreatorDepositFundDetails,
    normalizeCreatorDepositPreview,
    normalizeProfessionalType,
    parseCreatorDepositUnknownResponse,
    readCreatorDepositIntent,
    rememberCreatorDepositPaymentRequest,
    releaseCreatorDepositIntent,
} = creatorDeposit;
const CREATOR_DEPOSIT_TYPES = ["PHOTOGRAPHER", "MAKEUP_ARTIST"];
const creatorDepositRoleKey = (professionalType) => getCreatorDepositMeta(professionalType).roleKey;
const creatorDepositAccountKey = (rootState) => {
    const user = rootState && rootState.user || {};
    return user.user_id || (user.basic_data && user.basic_data.account ? `account:${user.basic_data.account}` : null);
};
const emptyCreatorDepositResource = (professionalType) => ({
    professionalType,
    status: "idle",
    error: "",
    data: null,
});
const emptyCreatorDepositDetail = () => ({
    professionalType: "",
    view: "details",
    preview: emptyCreatorDepositResource(""),
    channels: emptyCreatorDepositResource(""),
    fundDetails: emptyCreatorDepositResource(""),
    payments: emptyCreatorDepositResource(""),
    activePayment: null,
    creatingPayment: false,
    creationError: "",
    acceptingAgreement: false,
    acceptanceError: "",
});
const emptyCreatorRevenueCenter = () => ({
    status: "idle",
    error: "",
    revenues: [],
    selected: null,
    roleIndex: {},
    depositAccounts: {
        photographer: emptyCreatorDepositResource("PHOTOGRAPHER"),
        makeup: emptyCreatorDepositResource("MAKEUP_ARTIST"),
    },
    depositDetail: emptyCreatorDepositDetail(),
});
const executeCreatorDepositPaymentCommand = ({
    requestPath,
    submission,
    failureMessage,
    storage,
    accountId,
    professionalType,
    commit,
    mutation,
}) => request.post(requestPath, submission)
    .then((response) => parseCreatorDepositUnknownResponse(response)
        || normalizeCreatorDepositPaymentRequest(unwrapResult(response, failureMessage)))
    .catch((error) => {
        const unknown = parseCreatorDepositUnknownResponse(error);
        if (unknown) return unknown;
        const rawPayment = error && error.response && error.response.data && error.response.data.data;
        const failedPayment = rawPayment ? normalizeCreatorDepositPaymentRequest(rawPayment) : null;
        if (failedPayment && failedPayment.paymentRequestNo && isCreatorDepositPaymentTerminal(failedPayment)) {
            releaseCreatorDepositIntent(storage, accountId, professionalType);
        }
        throw error;
    })
    .then((payment) => {
        commit(mutation, payment);
        if (isCreatorDepositPaymentTerminal(payment)) releaseCreatorDepositIntent(storage, accountId, professionalType);
        else rememberCreatorDepositPaymentRequest(storage, accountId, professionalType, payment);
        return payment;
    });
const loadCreatorRevenueOrdersPage = (pageNo = 1, orders = []) => request.get(API.photography.creatorServiceOrders, {
    params: buildCreatorServiceOrderQuery({ status: "ALL", sort: "accepted", pageNo, pageSize: 50 }),
}).then((response) => {
    const page = normalizeCreatorServiceOrderPage(unwrapResult(response, "收益身份信息加载失败"));
    const combined = orders.concat(page.items);
    return combined.length < page.total
        ? loadCreatorRevenueOrdersPage(pageNo + 1, combined)
        : combined;
});
const {
    BUSINESS_MODULE,
    PHOTOGRAPHY_APPOINTMENT_STATUS,
    PHOTOGRAPHY_BOOKING_STATUS,
    createAppointmentPayload,
    createBookingIntent,
    createPhotographyVenueAddress,
    createProfessionalIdentityPrevalidationPayload,
    createQuotePayload,
    getAppointmentFailure,
    getQuoteFailureStatus,
    isAppointmentPriceChangeError,
    isChinaMainlandPhone,
    normalizePhotographyQuote,
    normalizePhotographyAppointment,
    normalizeDirectedCreator,
    normalizePhotographyServiceItem,
    normalizePhotographyVenues,
} = photographyBooking;
const emptyBooking = () => ({ serviceItem: null, venues: [], campusStatus: "UNKNOWN", quote: null, couponCandidates: [], couponQuoteSignature: "" });
const creationOwner = rootState => {
    const user = rootState && rootState.user;
    return user ? `${user.is_login === true}:${user.user_id}:${user.sessionVersion}` : "";
};
// Account identity survives cold reload; sessionVersion remains an in-memory late-response guard.
const creationAccount = rootState => {
    const user = rootState && rootState.user;
    return user ? `${user.is_login === true}:${user.user_id}` : "";
};
const creationStorageKey = owner => `photography-create:${owner}`;
const saveCreationIntent = (owner, intent) => {
    try {
        const {contactSmsCode, ...payload} = intent.payload;
        const {fingerprint, ...stored} = intent;
        window.sessionStorage.setItem(creationStorageKey(owner),JSON.stringify({...stored,payload}));
    } catch(error) { /* The exact in-memory intent remains available. */ }
};
const forgetCreationIntent = owner => { try { window.sessionStorage.removeItem(creationStorageKey(owner)); } catch(error) { /* Storage may be unavailable. */ } };


const loadPhotographyCampusStatus = (required) => {
    if (!required) return Promise.resolve("UNKNOWN");
    return request.get(API.certifications.campus.qualification).then((response) => {
        if (!isResultSuccess(response)) {
            throw getResultError(response, "校园认证资格查询失败，请稍后重试");
        }
        return getResultData(response) === true ? "APPROVED" : "UNVERIFIED";
    });
};
const {
    createPaymentSwitchPayload,
    getBrowserPhotographyPaymentChannel,
    isDesktopPhotographyPaymentChannel,
    normalizeAvailablePaymentChannels,
    normalizePhotographyPaymentAppointment,
} = photographyPayment;
const emptyPayment = () => ({ status: "idle", error: "", appointment: null });
const createUnknownPaymentRecovery = (appointment) => {
    const expiresAt = appointment.paymentDeadline;
    const deadline = Date.parse(`${expiresAt.replace(" ", "T")}+08:00`);
    const paymentRemainingSeconds = Number.isFinite(deadline)
        ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
        : 0;
    return normalizePhotographyPaymentAppointment({
        appointmentNo: appointment.appointmentNo,
        appointmentStatus: APPOINTMENT.WAITING_PAYMENT,
        paymentStatus: PAYMENT.CREATE_UNKNOWN,
        paymentDeadline: expiresAt,
        paymentRemainingSeconds,
        currentPaymentRequest: {
            paymentRequestNo: appointment.paymentRequestNo,
            paymentStatus: PAYMENT.CREATE_UNKNOWN,
            channel: appointment.paymentChannel,
            invocationPayload: null,
            expiresAt,
        },
    });
};
const pendingPaymentSwitchPayloads = Object.create(null);
const getCurrentPhotographyPaymentChannel = () => getBrowserPhotographyPaymentChannel(typeof navigator === "undefined" ? {} : navigator);
const getPaymentSwitchPayload = (appointmentNo, paymentRequestNo = "", targetChannel = getCurrentPhotographyPaymentChannel()) => {
    const intentKey = [appointmentNo, paymentRequestNo, targetChannel].join(":");
    if (!pendingPaymentSwitchPayloads[intentKey]) {
        pendingPaymentSwitchPayloads[intentKey] = createPaymentSwitchPayload(
            appointmentNo,
            undefined,
            targetChannel,
        );
    }
    return { intentKey, payload: pendingPaymentSwitchPayloads[intentKey] };
};
const clearPaymentSwitchPayload = (intentKey) => { delete pendingPaymentSwitchPayloads[intentKey]; };
const PAYMENT_STATUS_PROGRESS = { [PAYMENT.CREATING]: 0, [PAYMENT.CREATE_UNKNOWN]: 1, [PAYMENT.PENDING]: 2, [PAYMENT.SUCCEEDED]: 3 };
const isFreshPaymentSwitchDetail = (appointment, acceptedRequest) => {
    if (!appointment || !acceptedRequest) return false;
    if ([APPOINTMENT.PAID, APPOINTMENT.CLOSED].includes(appointment.appointmentStatus)) return true;
    const current = appointment.currentPaymentRequest;
    if (!current || current.paymentRequestNo !== acceptedRequest.paymentRequestNo) return false;
    return Number(PAYMENT_STATUS_PROGRESS[current.paymentStatus])
        >= Number(PAYMENT_STATUS_PROGRESS[acceptedRequest.paymentStatus]);
};

export default {
    state: {
        catalog: emptyCatalog(),
        catalogStatus: PHOTOGRAPHY_CATALOG_STATUS.IDLE,
        catalogError: "",
        themeDetail: null,
        themeDetailStatus: PHOTOGRAPHY_CATALOG_STATUS.IDLE,
        themeDetailError: "",
        booking: emptyBooking(),
        bookingLoadStatus: PHOTOGRAPHY_BOOKING_STATUS.IDLE,
        bookingLoadError: "",
        quoteStatus: PHOTOGRAPHY_BOOKING_STATUS.IDLE,
        quoteError: "",
        quoteRequestId: 0,
        appointmentStatus: PHOTOGRAPHY_APPOINTMENT_STATUS.IDLE,
        appointmentError: "",
        appointmentResult: null,
        appointmentPriceChange: null,
        bookingIntent: null,
        appointmentConfirmationDraft: null,
        paymentChannels: [],
        paymentChannelsStatus: "idle",
        paymentChannelsError: "",
        payment: emptyPayment(),
        creatorOrderHall: emptyCreatorOrderHall(),
        creatorServiceOrders: emptyCreatorServiceOrders(),
        creatorRevenueCenter: emptyCreatorRevenueCenter(),
    },
    mutations: {
        clearCreatorServiceOrderDetail(state) {
            state.creatorServiceOrders.detailRequestId += 1;
            state.creatorServiceOrders.selected = null;
        },
        clearUser(state) {
            state.booking = emptyBooking(); state.bookingIntent = null; state.appointmentResult = null;
            state.appointmentStatus = PHOTOGRAPHY_APPOINTMENT_STATUS.IDLE; state.appointmentError = "";
            state.payment = emptyPayment(); state.paymentChannels = [];
            state.creatorServiceOrders = emptyCreatorServiceOrders();
            state.creatorRevenueCenter = emptyCreatorRevenueCenter();
            state.appointmentConfirmationDraft = null;
        },
        setCreatorServiceOrderContactCleared(state) {
            if (state.creatorServiceOrders.selected) state.creatorServiceOrders.selected.contactEntry.actionUrl = '';
        },
        startPhotographyCatalogLoad(state) {
            state.catalogStatus = PHOTOGRAPHY_CATALOG_STATUS.LOADING;
            state.catalogError = "";
        },
        finishPhotographyCatalogLoad(state, catalog) {
            state.catalog = catalog;
            state.catalogStatus = catalog.themeTypes.length
                ? PHOTOGRAPHY_CATALOG_STATUS.READY
                : PHOTOGRAPHY_CATALOG_STATUS.EMPTY;
            state.catalogError = "";
        },
        failPhotographyCatalogLoad(state, { status, message }) {
            state.catalog = emptyCatalog();
            state.catalogStatus = status;
            state.catalogError = message;
        },
        startPhotographyThemeDetailLoad(state) {
            state.themeDetail = null;
            state.themeDetailStatus = PHOTOGRAPHY_CATALOG_STATUS.LOADING;
            state.themeDetailError = "";
        },
        finishPhotographyThemeDetailLoad(state, detail) {
            state.themeDetail = detail;
            state.themeDetailStatus = PHOTOGRAPHY_CATALOG_STATUS.READY;
            state.themeDetailError = "";
        },
        selectPhotographyThemeServiceItem(state, serviceItem) {
            state.booking = { ...state.booking, serviceItem };
            state.bookingLoadStatus = PHOTOGRAPHY_BOOKING_STATUS.READY;
            state.bookingLoadError = "";
        },
        failPhotographyThemeDetailLoad(state, message) {
            state.themeDetail = null;
            state.themeDetailStatus = PHOTOGRAPHY_CATALOG_STATUS.ERROR;
            state.themeDetailError = message;
        },
        startPhotographyBookingLoad(state) {
            state.booking = emptyBooking();
            state.bookingLoadStatus = PHOTOGRAPHY_BOOKING_STATUS.LOADING;
            state.bookingLoadError = "";
            state.quoteStatus = PHOTOGRAPHY_BOOKING_STATUS.IDLE;
            state.quoteError = "";
        },
        finishPhotographyBookingLoad(state, booking) {
            state.booking = {...emptyBooking(),...booking};
            state.bookingLoadStatus = PHOTOGRAPHY_BOOKING_STATUS.READY;
            state.bookingLoadError = "";
        },
        failPhotographyBookingLoad(state, message) {
            state.booking = emptyBooking();
            state.bookingLoadStatus = PHOTOGRAPHY_BOOKING_STATUS.ERROR;
            state.bookingLoadError = message;
        },
        startPhotographyQuote(state) {
            state.quoteRequestId += 1;
            state.booking.couponCandidates = [];
            state.booking.quote = null;
            state.quoteStatus = PHOTOGRAPHY_BOOKING_STATUS.QUOTING;
            state.quoteError = "";
        },
        invalidatePhotographyQuote(state) {
            state.booking.couponCandidates = [];
            state.booking.quote = null;
            if (state.quoteStatus === PHOTOGRAPHY_BOOKING_STATUS.QUOTING) {
                state.quoteRequestId += 1;
            }
            state.quoteStatus = PHOTOGRAPHY_BOOKING_STATUS.IDLE;
            state.quoteError = "";
        },
        finishPhotographyQuote(state, { requestId, quote, candidates = [], signature = "" }) {
            if (state.quoteRequestId !== requestId) return;
            state.booking.quote = quote;
            state.booking.couponCandidates = candidates;
            state.booking.couponQuoteSignature = signature;
            state.quoteStatus = PHOTOGRAPHY_BOOKING_STATUS.READY;
            state.quoteError = "";
        },
        failPhotographyQuote(state, { requestId, status, message, candidates = [] }) {
            if (state.quoteRequestId !== requestId) return;
            state.booking.couponCandidates = candidates;
            state.booking.quote = null;
            state.quoteStatus = status;
            state.quoteError = message;
        },
        setPhotographyCampusStatus(state, status) {
            state.booking.campusStatus = status || "UNKNOWN";
        },
        startPhotographyAppointment(state, intent) {
            state.quoteRequestId += 1;
            state.bookingIntent = intent;
            state.appointmentStatus = PHOTOGRAPHY_APPOINTMENT_STATUS.SUBMITTING;
            state.appointmentError = "";
            state.appointmentPriceChange = null;
        },
        finishPhotographyAppointment(state, appointment) {
            state.appointmentStatus = PHOTOGRAPHY_APPOINTMENT_STATUS.SUCCEEDED;
            state.appointmentResult = appointment;
            state.appointmentError = "";
            if (appointment.paymentStatus === PAYMENT.CREATE_UNKNOWN) {
                state.payment = {
                    status: "ready",
                    error: "",
                    appointment: createUnknownPaymentRecovery(appointment),
                };
            }
        },
        failPhotographyAppointment(state, failure) {
            state.appointmentStatus = failure.kind;
            state.appointmentError = failure.message;
            state.appointmentPriceChange = failure.kind === PHOTOGRAPHY_APPOINTMENT_STATUS.PRICE_CHANGED ? failure : null;
        },
        confirmPhotographyPriceChange(state) {
            if (!state.appointmentPriceChange) return;
            state.booking.quote = state.appointmentPriceChange.currentQuote;
            state.appointmentPriceChange = null;
            state.appointmentStatus = PHOTOGRAPHY_APPOINTMENT_STATUS.IDLE;
            state.appointmentError = "";
        },
        setPhotographyAppointmentConfirmationDraft(state, draft) {
            state.appointmentConfirmationDraft = { ...draft };
        },
        clearPhotographyAppointmentConfirmationDraft(state) {
            state.appointmentConfirmationDraft = null;
        },
        clearPhotographyBooking(state) {
            state.booking = emptyBooking();
            state.bookingLoadStatus = PHOTOGRAPHY_BOOKING_STATUS.IDLE;
            state.bookingLoadError = "";
            state.quoteStatus = PHOTOGRAPHY_BOOKING_STATUS.IDLE;
            state.quoteError = "";
            state.quoteRequestId += 1;
            state.appointmentStatus = PHOTOGRAPHY_APPOINTMENT_STATUS.IDLE;
            state.appointmentError = "";
            state.appointmentResult = null;
            state.appointmentPriceChange = null;
            state.bookingIntent = null;
            state.paymentChannels = [];
            state.paymentChannelsStatus = "idle";
            state.paymentChannelsError = "";
        },
        startPhotographyPaymentChannelsLoad(state) {
            state.paymentChannelsStatus = "loading";
            state.paymentChannelsError = "";
        },
        finishPhotographyPaymentChannelsLoad(state, channels) {
            state.paymentChannels = channels;
            state.paymentChannelsStatus = "ready";
            state.paymentChannelsError = "";
        },
        failPhotographyPaymentChannelsLoad(state, message) {
            state.paymentChannels = [];
            state.paymentChannelsStatus = "error";
            state.paymentChannelsError = message;
        },
        startPhotographyPaymentLoad(state) {
            state.payment.status = "loading";
            state.payment.error = "";
        },
        finishPhotographyPaymentLoad(state, appointment) {
            state.payment.status = "ready";
            state.payment.error = "";
            state.payment.appointment = appointment;
        },
        applyPhotographyPaymentSwitchResult(state, paymentRequest) {
            if (!state.payment.appointment || !paymentRequest) return;
            state.payment.status = "ready";
            state.payment.error = "";
            state.payment.appointment = normalizePhotographyPaymentAppointment({
                ...state.payment.appointment,
                appointmentStatus: paymentRequest.paymentStatus === PAYMENT.SUCCEEDED
                    ? APPOINTMENT.PAID
                    : state.payment.appointment.appointmentStatus,
                paymentStatus: paymentRequest.paymentStatus,
                currentPaymentRequest: paymentRequest,
            });
        },
        failPhotographyPaymentLoad(state, message) {
            state.payment.status = "error";
            state.payment.error = message;
        },
        clearPhotographyPayment(state) {
            state.payment = emptyPayment();
        },
        startCreatorOrderHallLoad(state) {
            state.creatorOrderHall.status = CREATOR_HALL_STATUS.LOADING;
            state.creatorOrderHall.error = "";
        },
        finishCreatorOrderHallLoad(state, hall) {
            state.creatorOrderHall = {
                ...hall,
                status: hall.orders.length || hall.invitations.length
                    ? CREATOR_HALL_STATUS.READY
                    : CREATOR_HALL_STATUS.EMPTY,
                error: "",
            };
        },
        failCreatorOrderHallLoad(state, { status, message }) {
            state.creatorOrderHall.status = status;
            state.creatorOrderHall.error = message;
            state.creatorOrderHall.orders = [];
            state.creatorOrderHall.invitations = [];
        },
        startCreatorServiceOrdersLoad(state, criteria) {
            state.creatorServiceOrders.status = CREATOR_SERVICE_ORDER_STATUS.LOADING;
            state.creatorServiceOrders.error = "";
            state.creatorServiceOrders.criteria = { ...criteria };
        },
        finishCreatorServiceOrdersLoad(state, page) {
            state.creatorServiceOrders.orders = page.items;
            state.creatorServiceOrders.total = page.total;
            state.creatorServiceOrders.pageNo = page.pageNo;
            state.creatorServiceOrders.pageSize = page.pageSize;
            state.creatorServiceOrders.status = page.items.length
                ? CREATOR_SERVICE_ORDER_STATUS.READY
                : CREATOR_SERVICE_ORDER_STATUS.EMPTY;
            state.creatorServiceOrders.error = "";
        },
        failCreatorServiceOrdersLoad(state, { status, message }) {
            state.creatorServiceOrders.status = status;
            state.creatorServiceOrders.error = message;
            state.creatorServiceOrders.orders = [];
            state.creatorServiceOrders.total = 0;
        },
        setCreatorServiceOrderDetail(state, detail) {
            state.creatorServiceOrders.selected = detail;
        },
        startCreatorRevenueCenterLoad(state) {
            state.creatorRevenueCenter.status = "loading";
            state.creatorRevenueCenter.error = "";
        },
        finishCreatorRevenueCenterLoad(state, center) {
            state.creatorRevenueCenter = {
                ...state.creatorRevenueCenter,
                ...center,
                status: center.revenues.length ? "ready" : "empty",
                error: "",
                selected: null,
            };
        },
        failCreatorRevenueCenterLoad(state, { status, message }) {
            state.creatorRevenueCenter.status = status;
            state.creatorRevenueCenter.error = message;
            state.creatorRevenueCenter.revenues = [];
            state.creatorRevenueCenter.selected = null;
        },
        setCreatorRevenueDetail(state, detail) {
            state.creatorRevenueCenter.selected = detail;
        },
        startCreatorDepositAccountLoad(state, professionalType) {
            const roleKey = creatorDepositRoleKey(professionalType);
            state.creatorRevenueCenter.depositAccounts[roleKey] = {
                ...state.creatorRevenueCenter.depositAccounts[roleKey],
                professionalType,
                status: "loading",
                error: "",
            };
        },
        finishCreatorDepositAccountLoad(state, account) {
            const roleKey = creatorDepositRoleKey(account.professionalType);
            state.creatorRevenueCenter.depositAccounts[roleKey] = {
                professionalType: account.professionalType,
                status: "ready",
                error: "",
                data: account,
            };
        },
        failCreatorDepositAccountLoad(state, { professionalType, status, message }) {
            const roleKey = creatorDepositRoleKey(professionalType);
            state.creatorRevenueCenter.depositAccounts[roleKey] = {
                professionalType,
                status,
                error: message,
                data: null,
            };
        },
        selectCreatorDepositDetail(state, professionalType) {
            state.creatorRevenueCenter.depositDetail = {
                ...emptyCreatorDepositDetail(),
                professionalType,
                preview: emptyCreatorDepositResource(professionalType),
                fundDetails: emptyCreatorDepositResource(professionalType),
                payments: emptyCreatorDepositResource(professionalType),
            };
        },
        clearCreatorDepositDetail(state) {
            state.creatorRevenueCenter.depositDetail = emptyCreatorDepositDetail();
        },
        startCreatorDepositPreviewLoad(state, professionalType) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.preview = {
                professionalType,
                status: "loading",
                error: "",
                data: null,
            };
            state.creatorRevenueCenter.depositDetail.acceptanceError = "";
        },
        finishCreatorDepositPreviewLoad(state, preview) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== preview.professionalType) return;
            state.creatorRevenueCenter.depositDetail.preview = {
                professionalType: preview.professionalType,
                status: "ready",
                error: "",
                data: preview,
            };
        },
        failCreatorDepositPreviewLoad(state, { professionalType, message }) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.preview = {
                professionalType,
                status: "error",
                error: message,
                data: null,
            };
        },
        startCreatorDepositChannelsLoad(state, professionalType) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.channels = {
                professionalType, status: "loading", error: "", data: [],
            };
        },
        finishCreatorDepositChannelsLoad(state, { professionalType, channels }) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.channels = {
                professionalType, status: "ready", error: "", data: channels,
            };
        },
        failCreatorDepositChannelsLoad(state, { professionalType, message }) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.channels = {
                professionalType, status: "error", error: message, data: [],
            };
        },
        startCreatorDepositPaymentsLoad(state, professionalType) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.payments = {
                ...state.creatorRevenueCenter.depositDetail.payments,
                professionalType, status: "loading", error: "",
            };
        },
        finishCreatorDepositPaymentsLoad(state, { professionalType, payments }) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.payments = {
                professionalType, status: "ready", error: "", data: payments,
            };
            state.creatorRevenueCenter.depositDetail.activePayment = findActiveCreatorDepositPayment(payments);
        },
        failCreatorDepositPaymentsLoad(state, { professionalType, message }) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.payments = {
                ...state.creatorRevenueCenter.depositDetail.payments,
                professionalType, status: "error", error: message,
            };
        },
        startCreatorDepositPaymentCreation(state) {
            state.creatorRevenueCenter.depositDetail.creatingPayment = true;
            state.creatorRevenueCenter.depositDetail.creationError = "";
        },
        finishCreatorDepositPaymentCreation(state, payment) {
            state.creatorRevenueCenter.depositDetail.creatingPayment = false;
            state.creatorRevenueCenter.depositDetail.creationError = "";
            state.creatorRevenueCenter.depositDetail.activePayment = payment;
        },
        updateCreatorDepositPayment(state, payment) {
            const resource = state.creatorRevenueCenter.depositDetail.payments;
            const payments = Array.isArray(resource.data) ? resource.data.slice() : [];
            const index = payments.findIndex((item) => item.paymentRequestNo === payment.paymentRequestNo);
            if (index === -1) payments.unshift(payment);
            else payments.splice(index, 1, payment);
            state.creatorRevenueCenter.depositDetail.payments = { ...resource, status: "ready", error: "", data: payments };
            state.creatorRevenueCenter.depositDetail.activePayment = findActiveCreatorDepositPayment(payments);
        },
        failCreatorDepositPaymentCreation(state, message) {
            state.creatorRevenueCenter.depositDetail.creatingPayment = false;
            state.creatorRevenueCenter.depositDetail.creationError = message;
        },
        setCreatorDepositDetailView(state, view) {
            state.creatorRevenueCenter.depositDetail.view = view === "payments" ? "payments" : "details";
        },
        startCreatorDepositFundDetailsLoad(state, professionalType) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.fundDetails = { professionalType, status: "loading", error: "", data: null };
        },
        finishCreatorDepositFundDetailsLoad(state, { professionalType, details }) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.fundDetails = { professionalType, status: "ready", error: "", data: details };
        },
        failCreatorDepositFundDetailsLoad(state, { professionalType, message }) {
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) return;
            state.creatorRevenueCenter.depositDetail.fundDetails = { professionalType, status: "error", error: message, data: null };
        },
        setCreatorDepositAgreementSubmitting(state, submitting) {
            state.creatorRevenueCenter.depositDetail.acceptingAgreement = submitting;
            if (submitting) state.creatorRevenueCenter.depositDetail.acceptanceError = "";
        },
        failCreatorDepositAgreementAcceptance(state, message) {
            state.creatorRevenueCenter.depositDetail.acceptingAgreement = false;
            state.creatorRevenueCenter.depositDetail.acceptanceError = message;
        },
    },
    actions: {
        loadPhotographyCatalog({ commit }) {
            commit("startPhotographyCatalogLoad");
            return request.get(API.photography.catalog, {
                skipAuth: true,
                skipRefresh: true,
            }).then((response) => {
                if (!isResultSuccess(response)) {
                    throw getResultError(response, "摄影服务目录加载失败，请稍后重试");
                }
                const catalog = normalizePhotographyCatalog(response.data.data);
                commit("finishPhotographyCatalogLoad", catalog);
                return catalog;
            }).catch((error) => {
                commit("failPhotographyCatalogLoad", {
                    status: getCatalogRequestState(error),
                    message: getResultMessage(error, "摄影服务目录加载失败，请稍后重试"),
                });
                throw error;
            });
        },
        loadPhotographyThemeDetail({ commit }, { themeTypeId, themeId, serviceItemId = "" }) {
            commit("startPhotographyThemeDetailLoad");
            commit("startPhotographyBookingLoad");
            return request.get(API.photography.themeDetail(themeTypeId, themeId), { skipAuth: true, skipRefresh: true }).then((detailResponse) => {
                if (!isResultSuccess(detailResponse)) throw getResultError(detailResponse, "摄影主题详情加载失败，请稍后重试");
                const detail = normalizePhotographyThemeDetail(detailResponse.data.data);
                const selected = detail.serviceItems.find((item) => item.serviceItemId === String(serviceItemId))
                    || detail.serviceItems[0];
                if (!selected) throw new Error("该摄影主题暂无可售服务");
                return request.get(API.photography.venues, {
                    params: { serviceItemId: selected.serviceItemId },
                    skipAuth: true,
                    skipRefresh: true,
                }).then((venuesResponse) => {
                    if (!isResultSuccess(venuesResponse)) throw getResultError(venuesResponse, "摄影主题详情加载失败，请稍后重试");
                    const venues = normalizePhotographyVenues(venuesResponse.data.data);
                    commit("finishPhotographyThemeDetailLoad", detail);
                    commit("finishPhotographyBookingLoad", { serviceItem: selected, venues, campusStatus: "UNKNOWN", quote: null });
                    return detail;
                });
            }).catch((error) => {
                const message = getResultMessage(error, "摄影主题详情加载失败，请稍后重试");
                commit("failPhotographyThemeDetailLoad", message);
                commit("failPhotographyBookingLoad", message);
                throw error;
            });
        },
        async selectPhotographyThemeServiceItem({ state, commit }, serviceItemId) {
            const selected = state.themeDetail && state.themeDetail.serviceItems.find(
                (item) => item.serviceItemId === String(serviceItemId),
            );
            if (!selected) return null;
            commit("startPhotographyBookingLoad");
            try {
                const venuesResponse = await request.get(API.photography.venues, {
                    params: { serviceItemId: selected.serviceItemId },
                    skipAuth: true,
                    skipRefresh: true,
                });
                if (!isResultSuccess(venuesResponse)) throw getResultError(venuesResponse, "场地加载失败，请稍后重试");
                commit("finishPhotographyBookingLoad", {
                    serviceItem: selected,
                    venues: normalizePhotographyVenues(venuesResponse.data.data),
                    campusStatus: "UNKNOWN",
                    quote: null,
                });
                return selected;
            } catch (error) {
                commit("failPhotographyBookingLoad", getResultMessage(error, "场地加载失败，请稍后重试"));
                throw error;
            }
        },
        loadCreatorOrderHall({ commit }) {
            commit("startCreatorOrderHallLoad");
            return Promise.all([
                loadReadiness(API.photography.photographerReadiness, "photographer"),
                loadReadiness(API.photography.makeupReadiness, "makeup"),
            ]).then(([photographer, makeup]) => Promise.all([
                photographer.available
                    ? request.get(API.photography.creatorJobHall("PHOTOGRAPHER")).then((response) => unwrapResult(response, "摄影师公开岗位加载失败"))
                    : Promise.resolve([]),
                makeup.available
                    ? request.get(API.photography.creatorJobHall("MAKEUP_ARTIST")).then((response) => unwrapResult(response, "化妆师公开岗位加载失败"))
                    : Promise.resolve([]),
                request.get(API.photography.creatorInvitations).then((response) => unwrapResult(response, "定向邀约加载失败")),
            ]).then(([photographerJobs, makeupJobs, invitations]) => ({
                readiness: { photographer, makeup },
                orders: [...(Array.isArray(photographerJobs) ? photographerJobs : []), ...(Array.isArray(makeupJobs) ? makeupJobs : [])].map(normalizeCreatorJob),
                invitations: (Array.isArray(invitations) ? invitations : []).map(normalizeCreatorInvitation),
            }))).then((hall) => {
                commit("finishCreatorOrderHallLoad", hall);
                return hall;
            }).catch((error) => {
                commit("failCreatorOrderHallLoad", {
                    status: getCreatorHallRequestState(error),
                    message: getResultMessage(error, "接单大厅加载失败，请稍后重试"),
                });
                throw error;
            });
        },
        acceptCreatorJob({ dispatch }, serviceOrderNo) {
            return request.post(API.photography.acceptCreatorJob(serviceOrderNo)).then((response) => {
                const accepted = normalizeCreatorJob(unwrapResult(response, "接单失败，请稍后重试"));
                return refreshAfterMutation(dispatch, accepted);
            });
        },
        respondCreatorInvitation({ dispatch }, { serviceOrderNo, action }) {
            const path = action === "reject"
                ? API.photography.rejectCreatorInvitation(serviceOrderNo)
                : API.photography.acceptCreatorInvitation(serviceOrderNo);
            return request.post(path).then((response) => {
                const invitation = normalizeCreatorInvitation(unwrapResult(response, action === "reject" ? "拒绝邀约失败" : "接受邀约失败"));
                return refreshAfterMutation(dispatch, invitation);
            });
        },
        loadCreatorServiceOrders({ commit, state, rootState }, criteria = {}) {
            const owner = rootState.user && rootState.user.user_id;
            const snapshot = state.creatorServiceOrders;
            const query = buildCreatorServiceOrderQuery(criteria);
            commit("startCreatorServiceOrdersLoad", criteria);
            const loading = String(criteria.status || "").toUpperCase() === "DISPUTE"
                ? loadCreatorDisputeOrders(criteria)
                : request.get(API.photography.creatorServiceOrders, { params: query })
                    .then((response) => normalizeCreatorServiceOrderPage(unwrapResult(response, "创作者服务订单加载失败")));
            return loading.then((page) => {
                if (state.creatorServiceOrders !== snapshot || owner !== (rootState.user && rootState.user.user_id)) throw new Error('登录身份已变化，请重新加载');
                commit("finishCreatorServiceOrdersLoad", page);
                return page;
            }).catch((error) => {
                if (state.creatorServiceOrders !== snapshot || owner !== (rootState.user && rootState.user.user_id)) throw error;
                commit("failCreatorServiceOrdersLoad", {
                    status: getCreatorServiceOrderRequestState(error),
                    message: getResultMessage(error, "创作者服务订单加载失败，请稍后重试"),
                });
                throw error;
            });
        },
        loadCreatorServiceOrderDetail({ commit, state, rootState }, serviceOrderNo) {
            const owner = rootState.user && rootState.user.user_id;
            const snapshot = state.creatorServiceOrders;
            commit('clearCreatorServiceOrderDetail');
            const requestId = snapshot.detailRequestId;
            return request.get(API.photography.creatorServiceOrderDetail(serviceOrderNo)).then(async (response) => {
                const raw = unwrapResult(response, "服务订单详情加载失败");
                const detail = normalizeCreatorServiceOrder(raw);
                if (state.creatorServiceOrders !== snapshot || requestId !== snapshot.detailRequestId || owner !== (rootState.user && rootState.user.user_id)) throw new Error('页面或登录身份已变化，请重新加载');
                commit("setCreatorServiceOrderDetail", detail);
                return detail;
            });
        },
        async loadCreatorSchedule() {
            const scheduled = unwrapResult(await request.get(API.photography.creatorSchedule), "日程读取失败");
            const active = [];
            let pageNo = 1;
            let total;
            do {
                const page = unwrapResult(await request.get(API.photography.creatorServiceOrders, {
                    params: buildCreatorServiceOrderQuery({ status: ASSIGNMENT.IN_SERVICE, pageNo, pageSize: 50 }),
                }), "服务中现场占用读取失败");
                const verified = await attachCreatorCompletionFacts(page);
                active.push(...verified.items.filter(item => item.fulfillment && item.fulfillment.serviceStatus === SERVICE.IN_SERVICE)
                    .map(item => normalizeCreatorServiceOrder(item)));
                total = page.total; pageNo += 1;
                if (!page.items.length) break;
            } while ((pageNo - 1) * 50 < total);
            // The schedule endpoint contains future ACCEPTED work only; overdue IN_SERVICE work still occupies the site.
            return [...new Map([...(Array.isArray(scheduled) ? scheduled : []).map(item => normalizeCreatorServiceOrder(item)), ...active]
                .map(item => [item.serviceOrderNo, item])).values()];
        },
        loadCreatorCompletion(_, serviceOrderNo) {
            return request.get(API.photography.creatorFulfillment(serviceOrderNo), { headers: { "Cache-Control": "no-store" } })
                .then(response => unwrapResult(response, "履约信息读取失败"));
        },
        async cancelCreatorServiceOrder({ dispatch }, { serviceOrderNo, reason }) {
            const explanation = String(reason || '').trim();
            if (!explanation || explanation.length > 200) throw new Error('请填写 1–200 字退出原因');
            const latest = await dispatch('loadCreatorServiceOrderDetail', serviceOrderNo);
            if (!latest.actions.some(a => a.code === creatorServiceOrder.ACTION_CODES.CANCEL_SERVICE)) throw new Error('当前承接状态不能退出，请刷新订单');
            return request.post(API.photography.cancelCreatorServiceOrder(serviceOrderNo), qs.stringify({ reason: explanation }))
                .then(response => unwrapResult(response, '退出承接失败'));
        },
        async requestCreatorCompletionReview({ dispatch }, { serviceOrderNo, reason }) {
            const explanation = String(reason || "").trim();
            if (!explanation || explanation.length > 200) throw new Error("请填写 1–200 字核实说明");
            const facts = await dispatch("loadCreatorCompletion", serviceOrderNo);
            if (facts.completionRequestedAt || facts.finishedAt) return facts;
            if (facts.serviceStatus !== SERVICE.IN_SERVICE || facts.canRequestCompletionReview !== true || facts.unresolvedDispute) throw new Error("当前不可申请核实，有未解决履约争议请通过争议处理流程核实");
            return request.post(API.photography.requestCompletionReview(serviceOrderNo), qs.stringify({ reason: explanation }))
                .then(response => unwrapResult(response, "核实申请提交失败"));
        },
        async registerCreatorArrival({ dispatch }, { serviceOrderNo, photo }) {
            const latest = await dispatch("loadCreatorServiceOrderDetail", serviceOrderNo);
            if (!latest.actions.some(action => action.code === creatorServiceOrder.ACTION_CODES.REGISTER_ARRIVAL)) throw new Error("当前服务不满足到场登记前置，请刷新订单");
            const submission = buildArrivalSubmission(photo);
            return request.post(API.photography.registerCreatorArrival(serviceOrderNo), submission).then((response) => {
                unwrapResult(response, "到场登记失败");
                return dispatch("loadCreatorServiceOrderDetail", serviceOrderNo);
            });
        },
        async checkInCreatorServiceOrder({ dispatch }, { serviceOrderNo, serviceCode }) {
            const latest = await dispatch('loadCreatorServiceOrderDetail', serviceOrderNo);
            if (!latest.actions.some(action => action.code === creatorServiceOrder.ACTION_CODES.CHECK_IN)) throw new Error('当前服务不满足核销前置，请刷新订单');
            return request.post(API.photography.checkInCreatorServiceOrder(serviceOrderNo), {
                serviceCode,
            }).then((response) => {
                unwrapResult(response, "到场核销失败");
                return dispatch("loadCreatorServiceOrderDetail", serviceOrderNo);
            });
        },
        loadCreatorRevenueRoleIndex() {
            return loadCreatorRevenueOrdersPage().then(buildCreatorRevenueRoleIndex);
        },
        async loadCreatorRevenueCenter({ commit, dispatch }) {
            commit("startCreatorRevenueCenterLoad");
            const accountLoads = CREATOR_DEPOSIT_TYPES.map((professionalType) => dispatch("loadCreatorDepositAccount", professionalType)
                .catch(() => null));
            let revenueResult;
            let revenueError;
            try {
                const [rawRevenues, roleIndex] = await Promise.all([
                request.get(API.photography.creatorRevenues).then((response) => unwrapResult(response, "创作者收益加载失败")),
                dispatch("loadCreatorRevenueRoleIndex"),
                ]);
                revenueResult = {
                    revenues: (Array.isArray(rawRevenues) ? rawRevenues : [])
                        .map((item) => normalizeCreatorRevenue(item, roleIndex)),
                    roleIndex,
                };
                commit("finishCreatorRevenueCenterLoad", revenueResult);
            } catch (error) {
                revenueError = error;
                commit("failCreatorRevenueCenterLoad", {
                    status: getErrorStatus(error) === 401 ? "session_expired" : "error",
                    message: getResultMessage(error, "创作者收益加载失败，请稍后重试"),
                });
            }
            await Promise.all(accountLoads);
            if (revenueError) throw revenueError;
            return revenueResult;
        },
        loadCreatorDepositAccount({ commit }, requestedType) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType) return Promise.reject(new Error("不支持的专业类型"));
            commit("startCreatorDepositAccountLoad", professionalType);
            return request.get(API.photography.creatorDepositAccount(professionalType)).then((response) => {
                const account = normalizeCreatorDepositAccount(unwrapResult(response, "履约保证金账户加载失败"));
                commit("finishCreatorDepositAccountLoad", account);
                return account;
            }).catch((error) => {
                const missing = getErrorStatus(error) === 404;
                commit("failCreatorDepositAccountLoad", {
                    professionalType,
                    status: missing ? "missing" : "error",
                    message: getResultMessage(error, missing ? "尚未取得该专业身份" : "履约保证金账户加载失败，请重试"),
                });
                throw error;
            });
        },
        loadCreatorDepositPreview({ commit }, requestedType) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType) return Promise.reject(new Error("不支持的专业类型"));
            commit("startCreatorDepositPreviewLoad", professionalType);
            return request.get(API.photography.creatorDepositPaymentPreview(professionalType)).then((response) => {
                const preview = normalizeCreatorDepositPreview(unwrapResult(response, "履约保证金应缴预览加载失败"));
                commit("finishCreatorDepositPreviewLoad", preview);
                return preview;
            }).catch((error) => {
                commit("failCreatorDepositPreviewLoad", {
                    professionalType,
                    message: getResultMessage(error, "履约保证金应缴预览加载失败，请重试"),
                });
                throw error;
            });
        },
        loadCreatorDepositPaymentChannels({ commit }, requestedType) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType) return Promise.reject(new Error("不支持的专业类型"));
            commit("startCreatorDepositChannelsLoad", professionalType);
            return request.get(API.photography.creatorDepositPaymentChannels(professionalType)).then((response) => {
                const channels = normalizeCreatorDepositPaymentChannels(unwrapResult(response, "履约保证金支付渠道加载失败"));
                commit("finishCreatorDepositChannelsLoad", { professionalType, channels });
                return channels;
            }).catch((error) => {
                commit("failCreatorDepositChannelsLoad", {
                    professionalType,
                    message: getResultMessage(error, "当前客户端无法使用履约保证金支付渠道"),
                });
                throw error;
            });
        },
        loadCreatorDepositFundDetails({ commit }, requestedType) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType) return Promise.reject(new Error("不支持的专业类型"));
            commit("startCreatorDepositFundDetailsLoad", professionalType);
            return request.get(API.photography.creatorDepositFundDetails(professionalType)).then((response) => {
                const details = normalizeCreatorDepositFundDetails(unwrapResult(response, "履约保证金资金明细加载失败"));
                commit("finishCreatorDepositFundDetailsLoad", { professionalType, details });
                return details;
            }).catch((error) => {
                commit("failCreatorDepositFundDetailsLoad", { professionalType, message: getResultMessage(error, "履约保证金资金明细加载失败，请重试") });
                throw error;
            });
        },
        loadCreatorDepositPayments({ commit, rootState }, requestedType) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType) return Promise.reject(new Error("不支持的专业类型"));
            commit("startCreatorDepositPaymentsLoad", professionalType);
            return request.get(API.photography.creatorDepositPayments(professionalType)).then((response) => {
                const rawPayments = unwrapResult(response, "履约保证金付款记录加载失败");
                const payments = (Array.isArray(rawPayments) ? rawPayments : []).map(normalizeCreatorDepositPaymentRequest);
                commit("finishCreatorDepositPaymentsLoad", { professionalType, payments });
                const storage = typeof window === "undefined" ? null : window.sessionStorage;
                const accountId = creatorDepositAccountKey(rootState);
                const intent = readCreatorDepositIntent(storage, accountId, professionalType);
                const intentPayment = intent && intent.paymentRequestNo
                    ? payments.find((payment) => payment.paymentRequestNo === intent.paymentRequestNo)
                    : null;
                if (intentPayment && isCreatorDepositPaymentTerminal(intentPayment)) {
                    releaseCreatorDepositIntent(storage, accountId, professionalType);
                }
                return payments;
            }).catch((error) => {
                commit("failCreatorDepositPaymentsLoad", {
                    professionalType,
                    message: getResultMessage(error, "履约保证金付款状态加载失败，请重试"),
                });
                throw error;
            });
        },
        createCreatorDepositPayment({ commit, rootState, state }, { professionalType: requestedType, channel }) {
            const professionalType = normalizeProfessionalType(requestedType);
            const accountId = creatorDepositAccountKey(rootState);
            const storage = typeof window === "undefined" ? null : window.sessionStorage;
            let submission;
            try {
                const preview = state.creatorRevenueCenter.depositDetail.preview.data || {};
                const agreementVersion = preview.agreement && preview.agreement.version || "";
                const intentFingerprint = `${preview.totalDue === undefined ? "" : preview.totalDue}|${agreementVersion}`;
                const idempotencyKey = getOrCreateCreatorDepositIntent(storage, accountId, professionalType, channel, intentFingerprint);
                submission = buildCreatorDepositPaymentSubmission(idempotencyKey, channel);
            } catch (error) {
                commit("failCreatorDepositPaymentCreation", getResultMessage(error, "履约保证金支付请求创建失败，请重试"));
                return Promise.reject(error);
            }
            commit("startCreatorDepositPaymentCreation");
            return executeCreatorDepositPaymentCommand({
                requestPath: API.photography.creatorDepositPaymentRequests(professionalType),
                submission,
                failureMessage: "履约保证金支付请求创建失败",
                storage,
                accountId,
                professionalType,
                commit,
                mutation: "finishCreatorDepositPaymentCreation",
            })
                .catch((error) => {
                    commit("failCreatorDepositPaymentCreation", getResultMessage(error, "履约保证金支付请求创建失败，请重试"));
                    throw error;
                });
        },
        switchCreatorDepositPayment({ commit, rootState, state }, { professionalType: requestedType, sourcePaymentRequestNo, channel }) {
            const professionalType = normalizeProfessionalType(requestedType);
            const detail = state.creatorRevenueCenter.depositDetail;
            const source = detail.activePayment && detail.activePayment.paymentRequestNo === sourcePaymentRequestNo
                ? detail.activePayment
                : (detail.payments.data || []).find((payment) => payment.paymentRequestNo === sourcePaymentRequestNo);
            const available = (detail.channels.data || []).some((item) => item.channel === Number(channel));
            if (!professionalType || !source || source.paymentStatus !== "PENDING" || source.channel === Number(channel) || !available) {
                return Promise.reject(new Error("请选择服务端当前提供的其他支付渠道"));
            }
            const storage = typeof window === "undefined" ? null : window.sessionStorage;
            const accountId = creatorDepositAccountKey(rootState);
            const preview = detail.preview.data || {};
            const agreementVersion = preview.agreement && preview.agreement.version || "";
            const intentFingerprint = `switch|${sourcePaymentRequestNo}|${preview.totalDue === undefined ? source.amount : preview.totalDue}|${agreementVersion}`;
            let submission;
            try {
                const idempotencyKey = getOrCreateCreatorDepositIntent(storage, accountId, professionalType, channel, intentFingerprint);
                submission = buildCreatorDepositPaymentSwitchSubmission(sourcePaymentRequestNo, idempotencyKey, channel);
            } catch (error) {
                return Promise.reject(error);
            }
            return executeCreatorDepositPaymentCommand({
                requestPath: API.photography.creatorDepositPaymentSwitch(professionalType),
                submission,
                failureMessage: "履约保证金支付渠道切换失败",
                storage,
                accountId,
                professionalType,
                commit,
                mutation: "updateCreatorDepositPayment",
            });
        },
        cancelCreatorDepositPayment({ commit, rootState }, { professionalType: requestedType, paymentRequestNo }) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType || !paymentRequestNo) return Promise.reject(new Error("支付请求信息不完整"));
            return request.post(API.photography.creatorDepositPaymentCancel(professionalType, paymentRequestNo))
                .then((response) => normalizeCreatorDepositPaymentRequest(unwrapResult(response, "履约保证金支付请求取消失败")))
                .then((payment) => {
                    commit("updateCreatorDepositPayment", payment);
                    if (isCreatorDepositPaymentTerminal(payment)) {
                        const storage = typeof window === "undefined" ? null : window.sessionStorage;
                        const accountId = creatorDepositAccountKey(rootState);
                        releaseCreatorDepositIntent(storage, accountId, professionalType);
                    }
                    return payment;
                });
        },
        verifyCreatorDepositPayment({ commit, rootState }, { professionalType: requestedType, paymentRequestNo }) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType || !paymentRequestNo) return Promise.reject(new Error("支付请求信息不完整"));
            return request.post(API.photography.creatorDepositPaymentVerify(professionalType, paymentRequestNo))
                .then((response) => normalizeCreatorDepositPaymentRequest(unwrapResult(response, "履约保证金支付结果核实失败")))
                .then((payment) => {
                    commit("updateCreatorDepositPayment", payment);
                    if (isCreatorDepositPaymentTerminal(payment)) {
                        const storage = typeof window === "undefined" ? null : window.sessionStorage;
                        releaseCreatorDepositIntent(storage, creatorDepositAccountKey(rootState), professionalType);
                    }
                    return payment;
                });
        },
        loadCreatorDepositDetail({ commit, dispatch }, requestedType) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType) return Promise.reject(new Error("不支持的专业类型"));
            commit("selectCreatorDepositDetail", professionalType);
            return Promise.all([
                dispatch("loadCreatorDepositAccount", professionalType).catch((error) => error),
                dispatch("loadCreatorDepositPreview", professionalType).catch((error) => error),
                dispatch("loadCreatorDepositPaymentChannels", professionalType).catch((error) => error),
                dispatch("loadCreatorDepositPayments", professionalType).catch((error) => error),
                dispatch("loadCreatorDepositFundDetails", professionalType).catch((error) => error),
            ]);
        },
        refreshCreatorDepositDetail({ dispatch }, requestedType) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType) return Promise.reject(new Error("不支持的专业类型"));
            const loads = [
                dispatch("loadCreatorDepositAccount", professionalType).catch((error) => error),
                dispatch("loadCreatorDepositPreview", professionalType).catch((error) => error),
                dispatch("loadCreatorDepositPaymentChannels", professionalType).catch((error) => error),
                dispatch("loadCreatorDepositPayments", professionalType).catch((error) => error),
                dispatch("loadCreatorDepositFundDetails", professionalType).catch((error) => error),
            ];
            return Promise.all(loads);
        },
        showCreatorDepositPayments({ commit, dispatch, state }, requestedType) {
            const professionalType = normalizeProfessionalType(requestedType);
            if (!professionalType) return Promise.reject(new Error("不支持的专业类型"));
            if (state.creatorRevenueCenter.depositDetail.professionalType !== professionalType) {
                return Promise.reject(new Error("请重新打开所选专业的履约保证金详情"));
            }
            commit("setCreatorDepositDetailView", "payments");
            return dispatch("loadCreatorDepositPayments", professionalType).catch((error) => error);
        },
        showCreatorDepositFundDetails({ commit }) {
            commit("setCreatorDepositDetailView", "details");
        },
        acceptCreatorDepositAgreement({ commit, dispatch, state }, requestedType) {
            const professionalType = normalizeProfessionalType(requestedType);
            const detail = state.creatorRevenueCenter.depositDetail;
            if (!professionalType || detail.professionalType !== professionalType || !detail.preview.data) {
                return Promise.reject(new Error("请先重新读取当前履约保证金协议"));
            }
            let submission;
            try {
                submission = buildCreatorDepositAgreementAcceptance(detail.preview.data);
            } catch (error) {
                return Promise.reject(error);
            }
            commit("setCreatorDepositAgreementSubmitting", true);
            return request.post(API.photography.creatorDepositAgreementAcceptances(professionalType), submission)
                .then((response) => unwrapResult(response, "履约保证金协议同意失败"))
                .then(() => dispatch("loadCreatorDepositPreview", professionalType))
                .then((preview) => {
                    commit("setCreatorDepositAgreementSubmitting", false);
                    return preview;
                })
                .catch((error) => {
                    commit("failCreatorDepositAgreementAcceptance", getResultMessage(error, "履约保证金协议同意失败，请重试"));
                    throw error;
                });
        },
        clearCreatorDepositDetail({ commit }) {
            commit("clearCreatorDepositDetail");
        },
        loadCreatorRevenueDetail({ commit, state }, serviceOrderNo) {
            return request.get(API.photography.creatorRevenueDetail(serviceOrderNo)).then((response) => {
                const detail = normalizeCreatorRevenueDetail(
                    unwrapResult(response, "服务订单收益详情加载失败"),
                    state.creatorRevenueCenter.roleIndex,
                );
                commit("setCreatorRevenueDetail", detail);
                return detail;
            });
        },
        clearCreatorRevenueDetail({ commit }) {
            commit("setCreatorRevenueDetail", null);
        },
        loadPhotographyBooking({ commit }, serviceItemId) {
            commit("startPhotographyBookingLoad");
            return Promise.all([
                request.get(API.photography.serviceItem(serviceItemId), { skipAuth: true, skipRefresh: true }),
                request.get(API.photography.venues, {
                    params: { serviceItemId },
                    skipAuth: true,
                    skipRefresh: true,
                }),
            ]).then(([serviceResponse, venuesResponse]) => {
                if (!isResultSuccess(serviceResponse) || !isResultSuccess(venuesResponse)) {
                    throw getResultError(!isResultSuccess(serviceResponse) ? serviceResponse : venuesResponse, "预约配置加载失败，请稍后重试");
                }
                const serviceItem = normalizePhotographyServiceItem(serviceResponse.data.data);
                const venues = normalizePhotographyVenues(venuesResponse.data.data);
                const booking = { serviceItem, venues, campusStatus: "UNKNOWN", quote: null };
                commit("finishPhotographyBookingLoad", booking);
                return booking;
            }).catch((error) => {
                commit("failPhotographyBookingLoad", getResultMessage(error, "预约配置加载失败，请稍后重试"));
                throw error;
            });
        },
        refreshPhotographyCampusQualification({ commit }, { required = false } = {}) {
            return loadPhotographyCampusStatus(required).then((status) => {
                commit("setPhotographyCampusStatus", status);
                return status;
            }).catch((error) => {
                commit("setPhotographyCampusStatus", "UNKNOWN");
                throw error;
            });
        },
        checkPhotographyRealNameQualification() {
            return request.get(API.user.realNameVerification.status).then((response) => {
                if (!isResultSuccess(response)) {
                    throw getResultError(response, "实名认证状态查询失败，请稍后重试");
                }
                const status = getResultData(response) || {};
                return status.authenticated === true;
            });
        },
        requestPhotographyQuote({ commit, state, rootState }, draft) {
            const owner = creationOwner(rootState);
            const candidates = state.booking.couponCandidates || [];
            const checkout = candidates.length > 0 || !!state.booking.couponQuoteSignature || draft.userCouponId != null;
            commit("startPhotographyQuote");
            const requestId = state.quoteRequestId;
            return request.post(API.photography.quotePreview, createQuotePayload(draft), {
                headers: { "Content-Type": "application/json" },
            }).then((response) => {
                if (!isResultSuccess(response)) throw getResultError(response, "摄影服务报价失败，请稍后重试");
                if (owner !== creationOwner(rootState) || state.quoteRequestId !== requestId) return null;
                const data = response.data.data;
                if (checkout && (!data || String(data.userCouponId == null ? "" : data.userCouponId) !== String(draft.userCouponId == null ? "" : draft.userCouponId)
                    || [data.totalAmount,data.discountAmount,data.payableAmount].some(value => value == null || !Number.isFinite(Number(value)) || Number(value)<0))) throw new Error("优惠试算结果不完整，请重试");
                const quote = normalizePhotographyQuote(data);
                commit("finishPhotographyQuote", { requestId, quote, candidates, signature: JSON.stringify(createQuotePayload(draft)) });
                return state.quoteRequestId === requestId ? quote : null;
            }).catch((error) => {
                if (owner !== creationOwner(rootState)) throw error;
                commit("failPhotographyQuote", {
                    requestId,
                    status: getQuoteFailureStatus(error),
                    message: getResultMessage(error, "摄影服务报价失败，请稍后重试"),
                });
                throw error;
            });
        },
        async requestPhotographyCouponCandidates({ commit, state, rootState }, draft) {
            const owner = creationOwner(rootState);
            commit("startPhotographyQuote");
            const requestId = state.quoteRequestId;
            const context = createQuotePayload(draft);
            delete context.userCouponId;
            if (context.pointId == null) delete context.pointId;
            let candidates = [];
            try {
                const response = await request.post(API.coupons.checkoutCandidates, { moduleCode: BUSINESS_MODULE.PHOTOGRAPHY.code, context }, {
                    headers: { "Content-Type": "application/json" },
                });
                if (owner !== creationOwner(rootState) || state.quoteRequestId !== requestId) return null;
                const data = unwrapResult(response, "优惠券读取失败，请重试");
                if (!data || !Array.isArray(data.items) || data.items.some(item => !item.coupon || !item.coupon.couponId || typeof item.available !== "boolean")) throw new Error("优惠券数据不完整，请重试");
                candidates = data.items;
                const chosen = data.items.find(item => String(item.coupon.couponId) === String(draft.userCouponId));
                if (draft.userCouponId != null && (!chosen || !chosen.available)) throw new Error(chosen && chosen.unavailableReason || "所选优惠券不可用，请更换或明确不使用优惠券");
                const selected = draft.userCouponId != null ? draft.userCouponId : null;
                const payload = { ...context, ...(selected != null ? { userCouponId: selected } : {}) };
                const quoted = unwrapResult(await request.post(API.photography.quotePreview, payload, {
                    headers: { "Content-Type": "application/json" },
                }), "优惠试算失败，请重试");
                if (owner !== creationOwner(rootState) || state.quoteRequestId !== requestId) return null;
                if (!quoted || [quoted.totalAmount, quoted.discountAmount, quoted.payableAmount].some(value => value == null || !Number.isFinite(Number(value)) || Number(value) < 0)
                    || String(quoted.userCouponId == null ? "" : quoted.userCouponId) !== String(selected == null ? "" : selected)) throw new Error("优惠试算结果不完整，请重试");
                const quote = normalizePhotographyQuote(quoted);
                commit("finishPhotographyQuote", { requestId, quote, candidates: data.items, signature: JSON.stringify(createQuotePayload({ ...draft, userCouponId: selected })) });
                return { quote, candidates: data.items, userCouponId: selected };
            } catch (error) {
                if (owner === creationOwner(rootState)) commit("failPhotographyQuote", { requestId, status: getQuoteFailureStatus(error), message: getResultMessage(error, "优惠试算失败，请重试"), candidates });
                throw error;
            }
        },
        prevalidatePhotographyProfessionalIdentity(context, draft) {
            return request.post(
                API.photography.professionalIdentityPrevalidation,
                createProfessionalIdentityPrevalidationPayload(draft),
                { headers: { "Content-Type": "application/json" } },
            ).then((response) => {
                if (!isResultSuccess(response)) {
                    throw getResultError(response, "专业身份码验证失败，请稍后重试");
                }
                return normalizeDirectedCreator(response.data.data);
            });
        },
        sendPhotographyAppointmentContactSmsCode(context, phone) {
            const contactPhone = String(phone || "").trim();
            if (!isChinaMainlandPhone(contactPhone)) return Promise.reject(new Error("请先填写有效的中国大陆手机号"));
            return request.post(API.photography.appointmentContactPhoneSmsCode, qs.stringify({ phone: contactPhone }))
                .then((response) => {
                    const result = unwrapResult(response, "验证码发送失败，请稍后重试");
                    return {
                        ...(result && typeof result === "object" ? result : {}),
                        smsCode: developmentSmsCode.extractDevelopmentSmsCode(response.data),
                    };
                });
        },
        loadPhotographyPaymentChannels({ commit }) {
            commit("startPhotographyPaymentChannelsLoad");
            return request.get(API.photography.paymentChannels, { headers: { "Cache-Control": "no-store" } })
                .then((response) => {
                    const channels = normalizeAvailablePaymentChannels(
                        unwrapResult(response, "预约支付渠道加载失败"),
                        getCurrentPhotographyPaymentChannel(),
                    );
                    commit("finishPhotographyPaymentChannelsLoad", channels);
                    return channels;
                }).catch((error) => {
                    commit("failPhotographyPaymentChannelsLoad",
                        getResultMessage(error, "预约支付渠道加载失败，请稍后重试"));
                    throw error;
                });
        },
        restorePhotographyCreationIntent({commit,state,rootState}) {
            const owner = creationAccount(rootState);
            try {
                const intent = JSON.parse(window.sessionStorage.getItem(creationStorageKey(owner)) || "null");
                if (!intent || intent.owner !== owner || !intent.payload || typeof intent.idempotencyKey !== "string" || intent.idempotencyKey !== intent.payload.idempotencyKey || !intent.quote) return null;
                commit("startPhotographyAppointment",intent);
                state.booking = {...emptyBooking(),...(intent.booking || {}),quote:intent.quote,couponCandidates:intent.candidates || []};
                commit("failPhotographyAppointment",{kind:PHOTOGRAPHY_APPOINTMENT_STATUS.RECOVERABLE,message:"原预约创建结果待恢复，请使用原提交意图核实；配置和优惠券已冻结。"});
                return {...intent.payload,contactSmsCode:""};
            } catch(error) { return null; }
        },
        createPhotographyAppointment({ commit, state, rootState }, draft) {
            const identity = () => creationOwner(rootState);
            const owner = identity();
            const account = creationAccount(rootState);
            const assertOwner = () => { if (identity() !== owner) throw new Error("登录身份已变化，请重新预约"); };
            if (state.appointmentStatus === PHOTOGRAPHY_APPOINTMENT_STATUS.SUBMITTING) return Promise.reject(new Error("预约正在创建，请稍候"));
            const recovering = state.appointmentStatus === PHOTOGRAPHY_APPOINTMENT_STATUS.RECOVERABLE && state.bookingIntent && state.bookingIntent.payload;
            let intent, payload;
            if (recovering) {
                intent = state.bookingIntent;
                if (intent.owner !== account) return Promise.reject(new Error("登录身份已变化，请重新预约"));
                payload = {...intent.payload,contactSmsCode:draft.contactSmsCode || intent.payload.contactSmsCode};
            } else {
                if (state.booking.couponQuoteSignature && state.booking.couponQuoteSignature !== JSON.stringify(createQuotePayload(draft))) return Promise.reject(new Error("配置已变化，请重新试算优惠"));
                let payable = state.booking.quote && state.booking.quote.payableAmount;
                if (draft.userCouponId != null) {
                    const candidate = (state.booking.couponCandidates || []).find(item => item.coupon.couponId === draft.userCouponId);
                    if (!candidate || !candidate.available || String(state.booking.quote && state.booking.quote.userCouponId) !== String(draft.userCouponId)) return Promise.reject(new Error("优惠券需要重新试算确认"));
                    payable = state.booking.quote.payableAmount;
                }
                if (payable == null) return Promise.reject(new Error("请先确认服务端报价"));
                const confirmedDraft = {...draft,confirmedPayableAmount:payable};
                intent = createBookingIntent(state.bookingIntent, confirmedDraft, state.booking.quote);
                payload = createAppointmentPayload(confirmedDraft,state.booking.quote,intent.idempotencyKey);
                intent = {...intent,payload,owner:account,quote:state.booking.quote,candidates:state.booking.couponCandidates || [],booking:{serviceItem:state.booking.serviceItem,venues:state.booking.venues,campusStatus:state.booking.campusStatus}};
            }
            saveCreationIntent(account,intent);
            commit("startPhotographyAppointment", intent);
            return request.post(API.photography.appointments, payload, {
                headers: { "Content-Type": "application/json" },
                timeout: 10000,
            }).then((response) => {
                assertOwner();
                const result = response && response.data || {};
                if (Number(response && response.status || result.code) === 202
                    && result.data && result.data.appointmentNo && result.data.paymentStatus === PAYMENT.CREATE_UNKNOWN) {
                    const appointment = normalizePhotographyAppointment(result.data);
                    forgetCreationIntent(account);
                    commit("finishPhotographyAppointment", appointment);
                    return appointment;
                }
                if (!isResultSuccess(response)) throw getResultError(response, "摄影预约创建失败，请稍后重试");
                if (!response.data.data || !response.data.data.appointmentNo) throw new Error("预约创建结果待恢复");
                const appointment = normalizePhotographyAppointment(response.data.data);
                forgetCreationIntent(account);
                commit("finishPhotographyAppointment", appointment);
                return appointment;
            }).catch((error) => {
                assertOwner();
                const previousQuote = state.booking.quote;
                const errorCode = Number(error && error.response && error.response.status || error.code);
                if (recovering || ![400,401,403,404,409,422].includes(errorCode)) {
                    commit("failPhotographyAppointment",{kind:PHOTOGRAPHY_APPOINTMENT_STATUS.RECOVERABLE,message:"预约创建结果待恢复，请保持原配置，使用原提交意图恢复结果。"});
                    throw error;
                }
                forgetCreationIntent(account);
                if (!isAppointmentPriceChangeError(error)) {
                    commit("failPhotographyAppointment", getAppointmentFailure(error, previousQuote));
                    throw error;
                }
                return request.post(API.photography.quotePreview, createQuotePayload(draft), {
                    headers: { "Content-Type": "application/json" },
                }).then((response) => {
                    assertOwner();
                    if (!isResultSuccess(response)) {
                        const quoteError = getResultError(response, "最新摄影服务报价获取失败，请稍后重试");
                        commit("failPhotographyAppointment", getAppointmentFailure(quoteError, previousQuote));
                        throw quoteError;
                    }
                    const currentQuote = normalizePhotographyQuote(response.data.data);
                    commit("failPhotographyAppointment", getAppointmentFailure(error, previousQuote, currentQuote));
                    throw error;
                }, (quoteError) => {
                    assertOwner();
                    commit("failPhotographyAppointment", getAppointmentFailure(quoteError, previousQuote));
                    throw quoteError;
                });
            });
        },
        loadPhotographyAppointmentPayment({ commit }, appointmentNo) {
            commit("startPhotographyPaymentLoad");
            return request.get(API.photography.appointmentDetail(appointmentNo)).then((response) => {
                if (!isResultSuccess(response)) throw getResultError(response, "摄影预约支付状态加载失败");
                const appointment = normalizePhotographyPaymentAppointment(response.data.data);
                commit("finishPhotographyPaymentLoad", appointment);
                return appointment;
            }).catch((error) => {
                commit("failPhotographyPaymentLoad", getResultMessage(error, "摄影预约支付状态加载失败，请稍后重试"));
                throw error;
            });
        },
        verifyPhotographyAppointmentPayment(context, { appointmentNo, paymentRequestNo }) {
            if (!appointmentNo || !paymentRequestNo) return Promise.reject(new Error("支付请求信息不完整"));
            return request.post(API.photography.appointmentPaymentVerify(appointmentNo, paymentRequestNo))
                .then((response) => unwrapResult(response, "预约支付结果核实失败"));
        },
        loadPhotographyPaymentVenue(context, { serviceItemId, venueId, pointId }) {
            return request.get(API.photography.venues, {
                params: { serviceItemId },
                skipAuth: true,
                skipRefresh: true,
            }).then((response) => {
                const venues = normalizePhotographyVenues(unwrapResult(response, "拍摄场地加载失败"));
                const venue = venues.find((item) => String(item.id) === String(venueId));
                if (!venue) throw new Error("预约拍摄场地已不可用");
                const point = pointId === null || pointId === undefined || pointId === ""
                    ? null
                    : venue.points.find((item) => String(item.id) === String(pointId));
                return {
                    venueName: venue.name,
                    pointName: point ? point.name : "",
                    fullAddress: createPhotographyVenueAddress(venue, point),
                };
            });
        },
        switchPhotographyAppointmentPayment({ commit, dispatch, state }, intent) {
            const appointmentNo = String(intent && intent.appointmentNo || "").trim();
            const paymentRequestNo = String(intent && intent.paymentRequestNo || "").trim();
            const targetChannel = String(intent && intent.targetChannel || "").trim();
            if (!appointmentNo || !paymentRequestNo || !isDesktopPhotographyPaymentChannel(targetChannel)) {
                return Promise.reject(new Error("支付渠道切换参数不正确"));
            }
            const active = state && state.payment && state.payment.appointment
                && state.payment.appointment.currentPaymentRequest;
            if (active && active.paymentRequestNo !== paymentRequestNo) {
                return Promise.reject(new Error("支付请求已变化，请刷新后重试"));
            }
            const { intentKey, payload } = getPaymentSwitchPayload(appointmentNo, paymentRequestNo, targetChannel);
            let responseStatus = 0;
            return request.post(API.photography.appointmentPaymentSwitch(appointmentNo), payload, {
                headers: { "Content-Type": "application/json" },
            }).then((response) => {
                const result = response && response.data || {};
                responseStatus = Number(response && response.status || result.code);
                const creationPending = responseStatus === 202 && result.data
                    && result.data.paymentStatus === PAYMENT.CREATE_UNKNOWN;
                if (!creationPending && !isResultSuccess(response)) {
                    throw getResultError(response, "支付方式更换失败，请稍后重试");
                }
                commit("applyPhotographyPaymentSwitchResult", result.data);
                const acceptedAppointment = state.payment.appointment;
                return dispatch("loadPhotographyAppointmentPayment", appointmentNo)
                    .then((appointment) => {
                        if (isFreshPaymentSwitchDetail(appointment, result.data)) return appointment;
                        commit("applyPhotographyPaymentSwitchResult", result.data);
                        return state.payment.appointment;
                    })
                    .catch(() => acceptedAppointment);
            }).then((appointment) => {
                if (responseStatus !== 202) clearPaymentSwitchPayload(intentKey);
                return appointment;
            });
        },
        retryPhotographyAlipayPayment({ dispatch }, appointmentNo) {
            const { intentKey, payload } = getPaymentSwitchPayload(appointmentNo);
            return request.post(API.photography.appointmentPaymentSwitch(appointmentNo),
                payload,
                { headers: { "Content-Type": "application/json" } },
            ).then((response) => {
                if (!isResultSuccess(response)) throw getResultError(response, "支付宝支付请求创建失败");
                return dispatch("loadPhotographyAppointmentPayment", appointmentNo);
            }).then((appointment) => {
                clearPaymentSwitchPayload(intentKey);
                return appointment;
            });
        },
    },
};
